// Per-line "Recommended vendors" panel. Hits /api/vendors/by-pn/:pn?platform=
// and shows three buckets in priority order: exact match → similar PN → spec-tagged.
// Collapsed by default so it doesn't clutter the line; broker expands when sourcing.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { Sparkles, ChevronRight, ExternalLink } from 'lucide-react';
import { api, type VendorByPnResponse } from '@/lib/api';

interface Props {
  partNumber: string;
  platform: string | null;
}

export function ByPnRecommendations({ partNumber, platform }: Props) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['by-pn', partNumber, platform],
    enabled: open && partNumber.length >= 2,
    queryFn: () => {
      const qs = platform ? `?platform=${encodeURIComponent(platform)}` : '';
      return api<VendorByPnResponse>(
        `/api/vendors/by-pn/${encodeURIComponent(partNumber)}${qs}`,
        { tokenGetter: () => getToken() },
      );
    },
    staleTime: 60_000,
  });

  const totalCount =
    (data?.exactMatch.length ?? 0) +
    (data?.similar.length ?? 0) +
    (data?.bySpec.length ?? 0);

  return (
    <div className="mt-3 border border-border rounded-md bg-primary-muted/30 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium hover:bg-primary-muted/60 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-primary-strong" />
          <span className="text-primary-strong">Recommended vendors</span>
          {open && data && (
            <span className="pill pill-info">{totalCount}</span>
          )}
        </span>
        <ChevronRight
          className={`w-3.5 h-3.5 text-foreground-muted transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {open && (
        <div className="px-3 py-2.5 border-t border-border space-y-3">
          {isLoading && <div className="text-xs text-foreground-muted">Loading…</div>}
          {isError && (
            <div className="text-xs text-danger">Failed to load recommendations.</div>
          )}
          {data && totalCount === 0 && (
            <div className="text-xs text-foreground-muted italic">
              No vendor history for this PN yet. Parse a vendor quote to start the trail.
            </div>
          )}

          {data && data.exactMatch.length > 0 && (
            <Bucket label="Quoted this PN before" tone="success" count={data.exactMatch.length}>
              {data.exactMatch.map((v) => (
                <ExactRow key={v.id} v={v} />
              ))}
            </Bucket>
          )}

          {data && data.similar.length > 0 && (
            <Bucket label="Quoted similar PNs" tone="primary" count={data.similar.length}>
              {data.similar.map((v) => (
                <SimilarRow key={v.id} v={v} />
              ))}
            </Bucket>
          )}

          {data && data.bySpec.length > 0 && (
            <Bucket label={`Tagged for ${data.platform ?? 'platform'}`} tone="warning" count={data.bySpec.length}>
              {data.bySpec.map((v) => (
                <BasicRow key={v.id} v={v} />
              ))}
            </Bucket>
          )}
        </div>
      )}
    </div>
  );
}

const TONE_CLASS: Record<string, string> = {
  success: 'text-success',
  primary: 'text-primary-strong',
  warning: 'text-warning',
};

function Bucket({
  label,
  tone,
  count,
  children,
}: {
  label: string;
  tone: 'success' | 'primary' | 'warning';
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-[10px] uppercase tracking-[0.06em] font-semibold ${TONE_CLASS[tone]}`}>
          {label}
        </span>
        <span className="text-[10px] text-foreground-muted">({count})</span>
      </div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

function ExactRow({
  v,
}: {
  v: {
    id: string;
    name: string;
    winRate: string;
    lastUnitPriceCents: number;
    lastCurrency: string;
    lastLeadTimeDays: number | null;
    lastQuoteAt: string;
  };
}) {
  return (
    <li className="flex items-baseline justify-between gap-2 text-xs px-2 py-1.5 rounded bg-surface border border-border">
      <Link href={`/vendors/${v.id}`} className="font-medium hover:text-primary truncate">
        {v.name}
      </Link>
      <div className="flex items-baseline gap-2 text-foreground-muted whitespace-nowrap">
        <span className="font-mono">
          {v.lastCurrency} {(v.lastUnitPriceCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        {v.lastLeadTimeDays != null && <span>· {v.lastLeadTimeDays}d</span>}
        <span>· {Number(v.winRate).toFixed(0)}% win</span>
      </div>
    </li>
  );
}

function SimilarRow({ v }: { v: { id: string; name: string; winRate: string; samplePartNumber: string } }) {
  return (
    <li className="flex items-baseline justify-between gap-2 text-xs px-2 py-1.5 rounded bg-surface border border-border">
      <Link href={`/vendors/${v.id}`} className="font-medium hover:text-primary truncate flex items-center gap-1.5">
        {v.name}
        <ExternalLink className="w-3 h-3" />
      </Link>
      <div className="flex items-baseline gap-2 text-foreground-muted whitespace-nowrap">
        <span className="font-mono">{v.samplePartNumber}</span>
        <span>· {Number(v.winRate).toFixed(0)}% win</span>
      </div>
    </li>
  );
}

function BasicRow({ v }: { v: { id: string; name: string; winRate: string; quoteCount: number } }) {
  return (
    <li className="flex items-baseline justify-between gap-2 text-xs px-2 py-1.5 rounded bg-surface border border-border">
      <Link href={`/vendors/${v.id}`} className="font-medium hover:text-primary truncate">
        {v.name}
      </Link>
      <div className="flex items-baseline gap-2 text-foreground-muted whitespace-nowrap">
        <span>{v.quoteCount} quote{v.quoteCount === 1 ? '' : 's'}</span>
        <span>· {Number(v.winRate).toFixed(0)}% win</span>
      </div>
    </li>
  );
}
